// ─── Atelier chassis ─────────────────────────────────────────────────────────
// The typed API client. One place that knows about the wire, so a contract
// change is one edit rather than a search across every page.

import type { ListResponse, ApiError } from "./types";

const BASE = "/api";

const TOKEN_KEY = "wine-cellar-log.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

/** Thrown for any non-2xx. Carries the server's code so callers can branch on
 *  the reason rather than string-matching a message. */
export class HttpError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(BASE + path, { ...init, headers });
  // A 401 on an ordinary request means the session is gone — expired, revoked,
  // or the account was deleted — and clearing the stale token is what stops
  // the app looping on a credential that will never work again.
  //
  // The sign-in endpoints are the exception, and it matters: there a 401 means
  // "that code is wrong", and blanket-mapping it would tell someone who
  // fat-fingered a digit that their session had expired — while they were in
  // the middle of creating one.
  const isSignIn = path.startsWith("/auth/request-code") || path.startsWith("/auth/verify");
  if (res.status === 401 && !isSignIn) {
    setToken(null);
    throw new HttpError(401, "unauthenticated", "Your session has expired. Sign in again.");
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // A non-JSON error body means something in front of the app answered —
      // a proxy, a 502 page. Say that rather than showing a parse error.
    }
    throw new HttpError(
      res.status,
      body?.error?.code ?? "http_error",
      body?.error?.message ?? `Request failed (${res.status})`,
      body?.error?.fields
    );
  }
  return (await res.json()) as T;
}

export type ListParams = {
  limit?: number;
  offset?: number;
  q?: string;
  sort?: string;
};

function qs(p: ListParams = {}): string {
  const s = new URLSearchParams();
  if (p.limit != null) s.set("limit", String(p.limit));
  if (p.offset != null) s.set("offset", String(p.offset));
  if (p.q) s.set("q", p.q);
  if (p.sort) s.set("sort", p.sort);
  const out = s.toString();
  return out ? `?${out}` : "";
}

/** One CRUD client per entity, all built from the same five calls. */
export function resource<T, TInput = Partial<T>>(route: string) {
  return {
    list: (p?: ListParams) => request<ListResponse<T>>(`/${route}${qs(p)}`),
    get: (id: string) => request<T>(`/${route}/${encodeURIComponent(id)}`),
    create: (body: TInput) =>
      request<T>(`/${route}`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<TInput>) =>
      request<T>(`/${route}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    remove: (id: string) =>
      request<void>(`/${route}/${encodeURIComponent(id)}`, { method: "DELETE" }),
  };
}

export const api = {
  regions: resource<import("./types").Region, import("./types").RegionInput>("regions"),
  producers: resource<import("./types").Producer, import("./types").ProducerInput>("producers"),
  bottles: resource<import("./types").Bottle, import("./types").BottleInput>("bottles"),
  tastingNotes: resource<import("./types").TastingNote, import("./types").TastingNoteInput>("tasting-notes"),
  auth: {
    requestCode: (email: string) =>
      request<{ sent: boolean }>("/auth/request-code", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    verify: (email: string, code: string) =>
      request<{ token: string; user: import("./types").User }>("/auth/verify", {
        method: "POST",
        body: JSON.stringify({ email, code }),
      }),
    me: () => request<import("./types").User>("/auth/me"),
    signOut: () => request<void>("/auth/signout", { method: "POST" }),
  },
};
